import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Target, ChevronRight, BarChart3, Building2, Layers } from "lucide-react";
import type { RadarConfig } from "@shared/schema";

interface PublicRadarConfig extends RadarConfig {
  company?: { id: string; name: string; slug?: string | null } | null;
  criteriaCount?: number;
}

export default function RadarPage() {
  const { data: radars = [], isLoading } = useQuery<PublicRadarConfig[]>({
    queryKey: ["/api/public/radars"],
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />

      {/* Hero */}
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-b">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-6 w-6 text-primary" />
              <span className="text-sm font-semibold text-primary uppercase tracking-wider">Engineering Match Intelligence</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
              Find the Right Solution<br className="hidden md:block" /> for Your Needs
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Our Radar evaluation tools help you assess how well a product or technology aligns
              with your specific engineering requirements — providing a structured, objective match score.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                <span>Weighted criteria scoring</span>
              </div>
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-primary" />
                <span>Spider chart visualisation</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                <span>Gap analysis & supplier CTA</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Radar Listing */}
      <main className="flex-1 max-w-7xl mx-auto px-4 py-12 w-full">
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-5 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3 mb-6" />
                  <Skeleton className="h-9 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : radars.length === 0 ? (
          <div className="text-center py-24">
            <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Radar Evaluations Available</h2>
            <p className="text-muted-foreground">Check back soon — new evaluations are added regularly.</p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold">Available Evaluations</h2>
              <p className="text-muted-foreground mt-1">
                {radars.length} evaluation{radars.length !== 1 ? "s" : ""} available
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {radars.map(radar => (
                <Card
                  key={radar.id}
                  className="flex flex-col hover:shadow-md transition-shadow group"
                  data-testid={`radar-card-${radar.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                          Radar
                        </p>
                        <CardTitle className="text-lg leading-tight group-hover:text-primary transition-colors">
                          {radar.title}
                        </CardTitle>
                      </div>
                    </div>
                    {radar.productCategory && (
                      <Badge variant="secondary" className="w-fit text-xs">
                        {radar.productCategory}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1 gap-4">
                    <div>
                      <p className="font-medium text-sm">{radar.productName}</p>
                      {radar.productDescription && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-3">
                          {radar.productDescription}
                        </p>
                      )}
                    </div>

                    {radar.company && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Building2 className="h-3.5 w-3.5 shrink-0" />
                        <span>{radar.company.name}</span>
                      </div>
                    )}

                    <div className="mt-auto pt-2">
                      <Button asChild className="w-full" data-testid={`button-start-radar-${radar.id}`}>
                        <Link href={`/radar/${radar.slug}/evaluate`}>
                          Start Evaluation
                          <ChevronRight className="ml-1.5 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
